import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { tenantApi, type Tenant, type TenantStatus } from "@/lib/api.js";
import { ConfirmDialog } from "@/components/ConfirmDialog.js";

type FilterTab = "all" | "active" | "inactive";

const FILTER_LABELS: Record<FilterTab, string> = {
  all: "전체",
  active: "활성",
  inactive: "비활성",
};

function safeJson(s: string): Record<string, string> {
  try { return JSON.parse(s) as Record<string, string>; } catch { return {}; }
}

function getTenantKoName(tenant: Tenant): string {
  return safeJson(tenant.name).ko ?? tenant.slug;
}

function StatusBadge({ status }: { status: TenantStatus }) {
  if (status === "active") {
    return (
      <span className="rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700">
        활성
      </span>
    );
  }
  return (
    <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-500">
      비활성
    </span>
  );
}

function SortableRow({
  tenant,
  onToggle,
  onDelete,
  isPending,
}: {
  tenant: Tenant;
  onToggle: (t: Tenant) => void;
  onDelete: (t: Tenant) => void;
  isPending: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: tenant.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="hover:bg-gray-50/50 transition-colors"
    >
      <td className="px-3 py-4 w-8">
        <span
          {...attributes}
          {...listeners}
          className="cursor-grab text-gray-300 hover:text-gray-500 select-none text-lg"
          title="드래그해서 순서 변경"
        >
          ⠿
        </span>
      </td>
      <td className="px-4 py-4">
        <span className="font-mono text-sm text-gray-700">{tenant.slug}</span>
      </td>
      <td className="px-4 py-4">
        <span className="text-sm font-medium text-gray-900">{getTenantKoName(tenant)}</span>
      </td>
      <td className="px-4 py-4">
        <StatusBadge status={tenant.status} />
      </td>
      <td className="px-4 py-4">
        <span className="text-sm text-gray-400">
          {new Date(tenant.created_at).toLocaleDateString("ko-KR")}
        </span>
      </td>
      <td className="px-4 py-4">
        <div className="flex items-center justify-end gap-2">
          <Link
            to={`/tenants/${tenant.id}/edit`}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            수정
          </Link>
          <button
            onClick={() => onToggle(tenant)}
            disabled={isPending}
            className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${
              tenant.status === "active"
                ? "border-amber-200 text-amber-600 hover:bg-amber-50"
                : "border-green-200 text-green-600 hover:bg-green-50"
            }`}
          >
            {tenant.status === "active" ? "비활성화" : "활성화"}
          </button>
          <button
            onClick={() => onDelete(tenant)}
            className="rounded-lg px-2 py-1.5 text-xs text-red-400 hover:bg-red-50 hover:text-red-600"
          >
            삭제
          </button>
        </div>
      </td>
    </tr>
  );
}

export function TenantsPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<FilterTab>("all");
  const [deleteTarget, setDeleteTarget] = useState<Tenant | null>(null);
  const [localOrder, setLocalOrder] = useState<Tenant[]>([]);

  const { data: tenants, isLoading } = useQuery({
    queryKey: ["tenants", filter],
    queryFn: () => tenantApi.list(filter),
  });

  // 서버 데이터 → 로컬 순서 동기화
  useEffect(() => {
    if (tenants) {
      setLocalOrder([...tenants].sort((a, b) => a.display_order - b.display_order));
    }
  }, [tenants]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const reorderMutation = useMutation({
    mutationFn: (order: { id: string; display_order: number }[]) =>
      tenantApi.reorder(order),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenants"] });
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: TenantStatus }) =>
      tenantApi.setStatus(id, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenants"] });
    },
    onError: (err) => {
      alert(err instanceof Error ? err.message : "상태 변경에 실패했습니다.");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => tenantApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenants"] });
      setDeleteTarget(null);
    },
    onError: (err) => {
      alert(err instanceof Error ? err.message : "삭제에 실패했습니다.");
      setDeleteTarget(null);
    },
  });

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setLocalOrder((prev) => {
      const oldIdx = prev.findIndex((t) => t.id === active.id);
      const newIdx = prev.findIndex((t) => t.id === over.id);
      const next = arrayMove(prev, oldIdx, newIdx);
      reorderMutation.mutate(
        next.map((t, i) => ({ id: t.id, display_order: i }))
      );
      return next;
    });
  }

  return (
    <div className="flex-1 overflow-y-auto p-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">사찰 목록</h1>
        <Link
          to="/tenants/new"
          className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
        >
          + 사찰 추가
        </Link>
      </div>

      <div className="mb-5 flex gap-1 rounded-xl border border-gray-200 bg-white p-1 w-fit">
        {(["all", "active", "inactive"] as FilterTab[]).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setFilter(tab)}
            className={`rounded-lg px-4 py-1.5 text-sm font-medium transition-colors ${
              filter === tab
                ? "bg-brand-500 text-white shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {FILTER_LABELS[tab]}
          </button>
        ))}
      </div>

      <div className="rounded-2xl bg-white shadow-sm ring-1 ring-black/5 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-gray-400">불러오는 중...</div>
        ) : !localOrder.length ? (
          <div className="p-12 text-center">
            <p className="text-gray-400">등록된 사찰이 없습니다.</p>
            <Link
              to="/tenants/new"
              className="mt-3 inline-block text-sm font-medium text-brand-600 hover:underline"
            >
              + 첫 번째 사찰 추가하기
            </Link>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={localOrder.map((t) => t.id)} strategy={verticalListSortingStrategy}>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50 text-left">
                    <th className="px-3 py-3 w-8" />
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500">슬러그</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500">사찰명</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500">상태</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500">생성일</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 text-right">액션</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {localOrder.map((tenant) => (
                    <SortableRow
                      key={tenant.id}
                      tenant={tenant}
                      onToggle={(t) =>
                        toggleStatusMutation.mutate({
                          id: t.id,
                          status: t.status === "active" ? "inactive" : "active",
                        })
                      }
                      onDelete={setDeleteTarget}
                      isPending={toggleStatusMutation.isPending}
                    />
                  ))}
                </tbody>
              </table>
            </SortableContext>
          </DndContext>
        )}
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title="사찰 삭제"
        description={`"${deleteTarget ? getTenantKoName(deleteTarget) : ""}" 사찰을 삭제할까요? 이 작업은 되돌릴 수 없습니다.`}
        confirmLabel="삭제"
        danger
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
